import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext";
import {
  serverURL,
  postData,
  getData
} from "../../../../services/Fetchnodeservices";

// ─── ICONS ────────────────────────────────────────────────────
const UserPlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    style={{ animation: spinning ? 'spin 1s linear infinite' : 'none' }}
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────
const OfficialMember = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [tableReady, setTableReady] = useState(false);

  // Form state
  const [form, setForm] = useState({ mobile: '', password: '', name: '', email: '', role: 'Employee' });
  const [formErrors, setFormErrors] = useState({});
  const [accessEditor, setAccessEditor] = useState(null);
  const [restaurantReferrals, setRestaurantReferrals] = useState([]);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const OFFICIAL_PERMISSIONS = [
    ['dashboard.view', 'Dashboard'], ['restaurants.view', 'View restaurants'], ['restaurants.view_password', 'View restaurant password'], ['restaurants.create', 'Add restaurant'], ['restaurants.edit', 'Edit restaurant'],
    ['menu.view', 'View menu'], ['menu.manage', 'Add/edit menu'], ['orders.view', 'View orders'], ['orders.edit', 'Edit orders'],
    ['riders.view', 'View riders'], ['riders.edit', 'Edit riders'], ['locations.view', 'View locations'], ['locations.edit', 'Edit locations'],
    ['duties.view', 'View duties'], ['duties.manage', 'Manage duties'], ['members.view', 'View members'], ['members.create', 'Add members'], ['members.edit', 'Edit members'], ['referrals.view', 'View referral earnings'], ['cash.collect', 'Collect rider cash'], ['cash.verify', 'Verify rider cash'], ['cash.transit', 'View assigned bank trips'], ['cash.deposit', 'Deposit cash to bank'],
  ];
  const ROLE_PERMISSIONS = {
    TL: OFFICIAL_PERMISSIONS.map(([permission]) => permission),
    Employee: ['dashboard.view', 'restaurants.view', 'restaurants.edit', 'menu.view', 'orders.view', 'riders.view', 'locations.view', 'duties.view', 'referrals.view'],
  };

  // ── Auto-create table on first load ────────────────────────
  useEffect(() => {
    ensureTable();
  }, []);

  const ensureTable = async () => {
    try {
      const res = await postData('api/official-member/ensure-table', {});
      if (res.success) setTableReady(true);
    } catch (err) {
      console.error('ensure table error:', err);
    }
  };

  // ── Fetch members ──────────────────────────────────────────
  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await getData('api/official-member/list');
      if (res.success) setMembers(res.data);
    } catch (err) {
      console.error('fetch error:', err);
    }
    setLoading(false);
  };

  const fetchRestaurantReferrals = async () => {
    const res = await getData('api/official-referrals/admin/report');
    if (res?.success) setRestaurantReferrals(res.data || []);
  };

  useEffect(() => {
    if (tableReady) { fetchMembers(); fetchRestaurantReferrals(); }
  }, [tableReady]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMembers();
    setRefreshing(false);
  };

  // ── Form validation ────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!form.mobile.trim()) errors.mobile = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) errors.mobile = 'Enter a valid 10-digit mobile number';
    if (!form.password.trim()) errors.password = 'Password is required';
    else if (form.password.length < 4) errors.password = 'Password must be at least 4 characters';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Invalid email format';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Create member ──────────────────────────────────────────
  const handleCreate = async () => {
    if (!validateForm()) return;
    setCreateLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const res = await postData('api/official-member/create', {
        mobile: form.mobile.trim(),
        password: form.password.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        permissions: ROLE_PERMISSIONS[form.role] || ROLE_PERMISSIONS.Employee,
      });

      if (res.success) {
        setMessage({ text: res.message, type: 'success' });
        setForm({ mobile: '', password: '', name: '', email: '', role: 'Employee' });
        setFormErrors({});
        fetchMembers();
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
      } else {
        setMessage({ text: res.message, type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Network error, try again', type: 'error' });
    }
    setCreateLoading(false);
  };


  // ── Toggle active status ───────────────────────────────────
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const res = await postData('api/official-member/toggle-status', { id });
      if (res.success) {
        setMessage({ text: res.message, type: 'success' });
        fetchMembers();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      }
    } catch (err) {
      setMessage({ text: 'Failed to update status', type: 'error' });
    }
  };

  const handleMemberRoleChange = async (memberId, role) => {
    const res = await postData('api/official-member/update-access', { id: memberId, role, permissions: ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.Employee });
    if (res?.success) {
      setMessage({ text: res.message, type: 'success' });
      fetchMembers();
    } else {
      setMessage({ text: res?.message || 'Role update failed', type: 'error' });
    }
  };

  const parsePermissions = (permissions) => {
    if (Array.isArray(permissions)) return permissions;
    try { return JSON.parse(permissions || '[]'); } catch { return []; }
  };

  const openAccessEditor = (member) => {
    setAccessEditor({ id: member.id, name: member.name || member.mobile, role: member.role || 'Employee', permissions: parsePermissions(member.permissions) });
  };

  const toggleMemberPermission = (permission) => {
    setAccessEditor((previous) => ({
      ...previous,
      permissions: previous.permissions.includes(permission)
        ? previous.permissions.filter((item) => item !== permission)
        : [...previous.permissions, permission],
    }));
  };

  const saveMemberAccess = async () => {
    if (!accessEditor) return;
    const res = await postData('api/official-member/update-access', accessEditor);
    if (res?.success) {
      setMessage({ text: `Access saved for ${accessEditor.name}`, type: 'success' });
      setAccessEditor(null);
      fetchMembers();
    } else setMessage({ text: res?.message || 'Access update failed', type: 'error' });
  };

  // ── Delete member ──────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this official member?')) return;
    try {
      const res = await postData('api/official-member/delete', { id });
      if (res.success) {
        setMessage({ text: res.message, type: 'success' });
        fetchMembers();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      }
    } catch (err) {
      setMessage({ text: 'Failed to delete', type: 'error' });
    }
  };

  // ── Date format ────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // ── Styles (inline, theme-aware) ──────────────────────────
  const s = {
    page: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: '24px', flexWrap: 'wrap', gap: '12px'
    },
    title: { fontSize: '22px', fontWeight: 700, color: theme.textPrimary, margin: 0 },
    subtitle: { fontSize: '13px', color: theme.textSecondary, marginTop: '4px' },
    refreshBtn: {
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 16px', borderRadius: '8px', border: `1px solid ${theme.border}`,
      background: theme.surface, color: theme.textPrimary, cursor: 'pointer',
      fontSize: '13px', fontWeight: 500, transition: 'all 0.2s'
    },
    card: {
      background: theme.cardBackground || theme.surface,
      borderRadius: '14px',
      border: `1px solid ${theme.border}`,
      boxShadow: `0 2px 12px ${theme.shadow || 'rgba(0,0,0,0.06)'}`,
      padding: '24px',
      marginBottom: '24px',
    },
    cardTitle: {
      fontSize: '15px', fontWeight: 600, color: theme.textPrimary,
      marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px'
    },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '16px' },
    permissions: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '9px', margin: '14px 0 18px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '12px', fontWeight: 600, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: {
      padding: '10px 14px', borderRadius: '8px', fontSize: '14px',
      border: `1px solid ${theme.border}`, background: theme.inputBackground || theme.background,
      color: theme.textPrimary, outline: 'none', transition: 'border 0.2s'
    },
    inputError: { borderColor: '#ef4444 !important' },
    errorText: { fontSize: '11px', color: '#ef4444', marginTop: '2px' },
    btnCreate: {
      padding: '12px 28px', borderRadius: '8px', border: 'none',
      background: theme.primaryGradient || theme.primary, color: '#fff',
      fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
      display: 'flex', alignItems: 'center', gap: '8px'
    },
    btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      textAlign: 'left', padding: '12px 14px', fontSize: '11px', fontWeight: 700,
      color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.6px',
      borderBottom: `2px solid ${theme.border}`
    },
    td: {
      padding: '12px 14px', fontSize: '13px', color: theme.textPrimary,
      borderBottom: `1px solid ${theme.border}`, verticalAlign: 'middle'
    },
    badge: {
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600
    },
    badgeActive: { background: `${theme.success || '#10b981'}18`, color: theme.success || '#10b981' },
    badgeInactive: { background: `${theme.error || '#ef4444'}18`, color: theme.error || '#ef4444' },
    actionBtn: {
      padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      fontSize: '12px', fontWeight: 500, transition: 'all 0.2s', marginRight: '6px'
    },
    alert: {
      padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
      marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px'
    },
    alertSuccess: { background: `${theme.success || '#10b981'}15`, color: theme.success || '#10b981', border: `1px solid ${theme.success || '#10b981'}30` },
    alertError: { background: `${theme.error || '#ef4444'}15`, color: theme.error || '#ef4444', border: `1px solid ${theme.error || '#ef4444'}30` },
    emptyState: {
      textAlign: 'center', padding: '40px 20px', color: theme.textSecondary
    },
    spinnerWrap: { textAlign: 'center', padding: '30px' },
    noTableAlert: {
      padding: '12px 16px', borderRadius: '8px',
      background: `${theme.warning || '#f59e0b'}12`,
      color: theme.warning || '#f59e0b',
      border: `1px solid ${theme.warning || '#f59e0b'}30`,
      fontSize: '13px', marginBottom: '16px',
      display: 'flex', alignItems: 'center', gap: '8px'
    },
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        input:focus { border-color: ${theme.primary} !important; }
        button:hover { opacity: 0.9; }
      `}</style>

      {/* ── Header ─────────────────────────────────────────── */}
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Official Members</h2>
          <p style={s.subtitle}>Create & manage official member login accounts</p>
        </div>
        <button
          onClick={handleRefresh}
          style={s.refreshBtn}
          onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.primaryLight}
          onMouseLeave={(e) => e.currentTarget.style.background = theme.surface}
        >
          <RefreshIcon spinning={refreshing} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* ── Alert ──────────────────────────────────────────── */}
      {message.text && (
        <div style={{ ...s.alert, ...(message.type === 'success' ? s.alertSuccess : s.alertError) }}>
          {message.type === 'success' ? <CheckCircleIcon /> : <XCircleIcon />}
          {message.text}
        </div>
      )}

      {/* ── Create Form ────────────────────────────────────── */}
      <div style={s.card}>
        <div style={s.cardTitle}>
          <UserPlusIcon />
          Create New Official Member
        </div>

        {!tableReady && (
          <div style={s.noTableAlert}>
            ⚠️ Setting up database table... If this persists, refresh the page.
          </div>
        )}

        <div style={s.formGrid}>
          {/* Mobile */}
          <div style={s.formGroup}>
            <label style={s.label}>Mobile Number *</label>
            <input
              type="tel"
              placeholder="10-digit mobile number"
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) })}
              style={{ ...s.input, ...(formErrors.mobile ? s.inputError : {}) }}
              maxLength={10}
            />
            {formErrors.mobile && <span style={s.errorText}>{formErrors.mobile}</span>}
          </div>

          {/* Password */}
          <div style={s.formGroup}>
            <label style={s.label}>Password *</label>
            <input
              type="text"
              placeholder="Min. 4 characters"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              style={{ ...s.input, ...(formErrors.password ? s.inputError : {}) }}
            />
            {formErrors.password && <span style={s.errorText}>{formErrors.password}</span>}
          </div>

          {/* Name */}
          <div style={s.formGroup}>
            <label style={s.label}>Full Name</label>
            <input
              type="text"
              placeholder="Member's full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={s.input}
            />
          </div>

          {/* Email */}
          <div style={s.formGroup}>
            <label style={s.label}>Email</label>
            <input
              type="email"
              placeholder="email@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              style={{ ...s.input, ...(formErrors.email ? s.inputError : {}) }}
            />
            {formErrors.email && <span style={s.errorText}>{formErrors.email}</span>}
          </div>

          <div style={s.formGroup}>
            <label style={s.label}>Official App Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={s.input}>
              <option value="TL">TL</option>
              <option value="Employee">Employee</option>
            </select>
          </div>
        </div>

        {/* Show Credentials Info */}
        {form.mobile.trim().length === 10 && form.password.trim().length >= 4 && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
            background: `${theme.info || '#3b82f6'}10`, border: `1px solid ${theme.info || '#3b82f6'}25`,
            fontSize: '12px', color: theme.textSecondary
          }}>
            <strong>Login credentials for member:</strong><br />
            📱 Login ID (Mobile): <strong>{form.mobile.trim()}</strong> &nbsp;|&nbsp;
            🔒 Password: <strong>{form.password.trim()}</strong>
          </div>
        )}

        <button
          onClick={handleCreate}
          disabled={createLoading || !tableReady}
          style={{ ...s.btnCreate, ...(createLoading ? s.btnDisabled : {}) }}
        >
          <UserPlusIcon />
          {createLoading ? 'Creating...' : 'Create Official Member'}
        </button>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}><ShieldCheckIcon /> Employee Restaurant Referrals ({restaurantReferrals.length})</div>
        <div style={s.tableWrap}><table style={s.table}><thead><tr><th style={s.th}>Restaurant</th><th style={s.th}>Employee</th><th style={s.th}>Referral Code</th><th style={s.th}>City</th><th style={s.th}>Reward</th><th style={s.th}>Added</th></tr></thead><tbody>
          {restaurantReferrals.length ? restaurantReferrals.map((row) => <tr key={row.restaurant_id}><td style={s.td}>{row.organisation_name || '—'}<br/><small>{row.contact || ''}</small></td><td style={s.td}>{row.member_name || '—'}<br/><small>{row.member_mobile || ''} · {row.role || ''}</small></td><td style={s.td}>{row.referral_code || '—'}</td><td style={s.td}>{row.city || '—'}</td><td style={{ ...s.td, color: theme.success || '#10b981', fontWeight: 700 }}>₹{row.official_referral_reward || 0}</td><td style={s.td}>{formatDate(row.referral_at)}</td></tr>) : <tr><td colSpan="6" style={{ ...s.td, textAlign: 'center' }}>No employee-attributed restaurants yet.</td></tr>}
        </tbody></table></div>
      </div>

      {/* ── Members List ──────────────────────────────────── */}
      <div style={s.card}>
        <div style={{ ...s.cardTitle, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheckIcon />
            Official Members ({members.length})
          </div>
        </div>

        {loading ? (
          <div style={s.spinnerWrap}>
            <div style={{
              width: '36px', height: '36px',
              border: `3px solid ${theme.border}`,
              borderTop: `3px solid ${theme.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
            <p style={{ ...s.subtitle, marginTop: '12px' }}>Loading members...</p>
          </div>
        ) : members.length === 0 ? (
          <div style={s.emptyState}>
            <ShieldCheckIcon />
            <p style={{ marginTop: '12px', fontSize: '14px' }}>No official members yet</p>
            <p style={{ fontSize: '12px', marginTop: '4px' }}>Create one using the form above</p>
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>#</th>
                  <th style={s.th}>Mobile (Login ID)</th>
                  <th style={s.th}>Password</th>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Role</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Last Login</th>
                  <th style={s.th}>Created</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m, idx) => (
                  <React.Fragment key={m.id}>
                  <tr>
                    <td style={s.td}>{idx + 1}</td>
                    <td style={{ ...s.td, fontWeight: 600 }}>{m.mobile}</td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {visiblePasswords[m.id] ? (m.password || '—') : '••••••••'}
                      </span>
                      <button
                        onClick={() => setVisiblePasswords((previous) => ({ ...previous, [m.id]: !previous[m.id] }))}
                        title={visiblePasswords[m.id] ? 'Hide password' : 'Show password'}
                        style={{ ...s.actionBtn, marginLeft: '8px', background: `${theme.primary}14`, color: theme.primary }}
                      >
                        {visiblePasswords[m.id] ? 'Hide' : 'Show'}
                      </button>
                    </td>
                    <td style={s.td}><button onClick={() => navigate(`/dashboard/official-member/${m.id}`)} style={{ border: 0, background: 'transparent', padding: 0, color: theme.primary, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>{m.name || m.mobile || '—'}</button></td>
                    <td style={s.td}>{m.email || '—'}</td>
                    <td style={s.td}>
                      <select
                        value={m.role || 'Employee'}
                        onChange={(e) => handleMemberRoleChange(m.id, e.target.value)}
                        style={{ ...s.input, padding: '6px 8px', minWidth: '120px', fontSize: '12px' }}
                      >
                        <option value="TL">TL</option>
                        <option value="Employee">Employee</option>
                      </select>
                    </td>
                    <td style={s.td}>
                      <span
                        style={{
                          ...s.badge,
                          ...(m.is_active ? s.badgeActive : s.badgeInactive)
                        }}
                      >
                        {m.is_active ? <CheckCircleIcon /> : <XCircleIcon />}
                        {m.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...s.td, fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {formatDate(m.last_login_at)}
                    </td>
                    <td style={{ ...s.td, fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {formatDate(m.created_at)}
                    </td>
                    <td style={s.td}>
                      <button
                        onClick={() => openAccessEditor(m)}
                        style={{ ...s.actionBtn, background: `${theme.primary}14`, color: theme.primary }}
                      >
                        <ShieldCheckIcon /> Access
                      </button>
                      <button
                        onClick={() => handleToggleStatus(m.id, m.is_active)}
                        title={m.is_active ? 'Deactivate' : 'Activate'}
                        style={{
                          ...s.actionBtn,
                          background: m.is_active ? `${theme.error || '#ef4444'}12` : `${theme.success || '#10b981'}12`,
                          color: m.is_active ? theme.error || '#ef4444' : theme.success || '#10b981',
                        }}
                      >
                        {m.is_active ? <XCircleIcon /> : <CheckCircleIcon />}
                        {m.is_active ? 'Deactivate' : 'Activate'}
                      </button>

                      <button
                        onClick={() => handleDelete(m.id)}
                        title="Delete"
                        style={{
                          ...s.actionBtn,
                          background: `${theme.error || '#ef4444'}12`,
                          color: theme.error || '#ef4444',
                        }}
                      >
                        <TrashIcon />
                        Delete
                      </button>
                    </td>
                  </tr>
                  {accessEditor?.id === m.id && (
                    <tr>
                      <td colSpan="10" style={{ ...s.td, background: theme.surfaceHover || `${theme.primary}08` }}>
                        <div style={{ fontWeight: 700, marginBottom: '10px' }}>Access for {accessEditor.name}</div>
                        <div style={s.permissions}>
                          {OFFICIAL_PERMISSIONS.map(([permission, label]) => (
                            <label key={permission} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', border: `1px solid ${theme.border}`, borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={accessEditor.permissions.includes(permission)} onChange={() => toggleMemberPermission(permission)} />
                              {label}
                            </label>
                          ))}
                        </div>
                        <button onClick={saveMemberAccess} style={s.btnCreate}>Save Access</button>
                        <button onClick={() => setAccessEditor(null)} style={{ ...s.actionBtn, marginLeft: '8px', background: theme.surface, color: theme.textPrimary, border: `1px solid ${theme.border}` }}>Cancel</button>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficialMember;
